<template>
    <BCard class="guided-products-review-section mb-1">
        <template #header>
            <div class="guided-products-review-section__heading">
                <div>
                    <h5 class="mb-25">
                        {{ translate('Component(ResearchReviewProcess):products', 'Products') }}
                    </h5>
                    <p class="text-less-muted mb-0">
                        {{ translate(
                            'Component(ResearchReviewProcess):guided-products-description',
                            'Review the product information extracted from the document.',
                        ) }}
                    </p>
                </div>
                <BButton
                    v-if="allowAddProduct"
                    type="button"
                    variant="outline-primary"
                    class="guided-products-review-section__add"
                    @click="addProduct"
                >
                    <FeatherIcon icon="PlusIcon" size="14" class="mr-25" />
                    {{ translate('Component(ResearchReviewProcess):add-product', 'Add product') }}
                </BButton>
            </div>
        </template>

        <BAlert v-if="!products.length" variant="light-secondary" show class="mb-0">
            <div class="alert-body">
                {{ translate(
                    'Component(ResearchReviewProcess):no-products',
                    'No products were extracted from this document.',
                ) }}
            </div>
        </BAlert>

        <div v-else class="guided-products-review-section__rows">
            <div
                v-for="(product, productIndex) in products"
                :key="product.extractedProductId || `guided-product-${productIndex}`"
                class="guided-products-review-section__row"
            >
                <div class="guided-products-review-section__attributes">
                    <div
                        v-for="(attribute, attributeIndex) in productAttributes(product)"
                        :key="`${product.extractedProductId || productIndex}-${attributeIndex}-${attribute.type}`"
                        class="guided-products-review-section__attribute"
                    >
                        <div class="guided-products-review-section__attribute-heading">
                            <label
                                :for="attributeInputId(productIndex, attributeIndex)"
                                class="mb-0 font-weight-medium"
                            >
                                {{ translatedTypeLabel(attribute.type) }}
                            </label>
                            <BButton
                                v-if="productAttributes(product).length > 1"
                                type="button"
                                variant="flat-danger"
                                size="sm"
                                class="btn-icon guided-products-review-section__remove-attribute"
                                :title="translate('Component(Button):remove', 'Remove')"
                                @click="removeAttribute(productIndex, attributeIndex)"
                            >
                                <FeatherIcon icon="XIcon" size="13" />
                            </BButton>
                        </div>
                        <BFormInput
                            :id="attributeInputId(productIndex, attributeIndex)"
                            :ref="attributeInputRef(productIndex, attributeIndex)"
                            :value="attribute.value"
                            :state="attributeViolationMessages(productIndex, attributeIndex).length ? false : null"
                            :title="attributeViolationMessages(productIndex, attributeIndex).join('\n')"
                            @input="updateAttribute(productIndex, attributeIndex, $event)"
                        />
                    </div>
                </div>

                <div class="guided-products-review-section__actions">
                    <BDropdown
                        v-if="allowAddAttribute"
                        right
                        variant="outline-primary"
                        toggle-class="text-nowrap"
                    >
                        <template #button-content>
                            <FeatherIcon icon="PlusIcon" size="13" class="mr-25" />
                            {{ translate('Component(ResearchReviewProcess):add-attribute', 'Add attribute') }}
                        </template>
                        <BDropdownItem
                            v-for="option in translatedTypeOptions"
                            :key="option.value"
                            @click="addAttribute(productIndex, option.value)"
                        >
                            {{ option.label }}
                        </BDropdownItem>
                    </BDropdown>
                    <div class="guided-products-review-section__remove-product">
                        <BButton
                            type="button"
                            variant="outline-danger"
                            class="btn-icon"
                            :title="translate('Component(Button):remove', 'Remove')"
                            @click="removeProduct(productIndex)"
                        >
                            <FeatherIcon icon="Trash2Icon" size="16" />
                        </BButton>
                    </div>
                </div>
            </div>
        </div>
    </BCard>
</template>

<script>
import {
    BAlert,
    BButton,
    BCard,
    BDropdown,
    BDropdownItem,
    BFormInput,
} from 'bootstrap-vue'
import { v4 as uuidv4 } from 'uuid'
import {
    PRODUCT_ATTRIBUTE_TYPES,
    productAttributeTypeLabel,
    productAttributes,
} from './product-attributes'
import { hasIdentity } from './product-review-view-model'

const clone = value => JSON.parse(JSON.stringify(value || []))

export default {
    name: 'GuidedProductsReviewSection',
    components: {
        BAlert,
        BButton,
        BCard,
        BDropdown,
        BDropdownItem,
        BFormInput,
    },
    props: {
        products: { type: Array, default: () => [] },
        links: { type: Array, default: () => [] },
        violations: { type: Array, default: () => [] },
        reviewContext: { type: Object, default: () => ({}) },
        workspaceService: { type: Object, required: true },
        allowAddProduct: { type: Boolean, default: true },
        allowAddAttribute: { type: Boolean, default: true },
    },
    computed: {
        translatedTypeOptions() {
            return PRODUCT_ATTRIBUTE_TYPES.map(option => ({
                ...option,
                label: this.translatedTypeLabel(option.value),
            }))
        },
    },
    methods: {
        productAttributes,
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        translatedTypeLabel(type) {
            return this.translate(
                `Component(ResearchReviewProductAttributeType):${type}`,
                productAttributeTypeLabel(type),
            )
        },
        emitState({ products = this.products, links = this.links }) {
            this.$emit('update', {
                products: clone(products),
                links: clone(links),
            })
        },
        addProduct() {
            if (!this.allowAddProduct) {
                return
            }

            const productIndex = this.products.length
            this.emitState({
                products: this.products.concat({
                    extractedProductId: uuidv4(),
                    attributes: [{ type: 'product_name', value: '', evidence: null }],
                }),
            })
            this.focusAttribute(productIndex, 0)
        },
        updateAttribute(productIndex, attributeIndex, value) {
            this.emitState({
                products: this.products.map((product, currentProductIndex) => (
                    currentProductIndex === productIndex
                        ? {
                            ...product,
                            attributes: productAttributes(product).map((attribute, currentAttributeIndex) => (
                                currentAttributeIndex === attributeIndex
                                    ? { ...attribute, value }
                                    : attribute
                            )),
                        }
                        : product
                )),
            })
        },
        addAttribute(productIndex, type) {
            if (!this.allowAddAttribute) {
                return
            }

            const attributeIndex = productAttributes(this.products[productIndex]).length
            this.emitState({
                products: this.products.map((product, currentProductIndex) => (
                    currentProductIndex === productIndex
                        ? {
                            ...product,
                            attributes: productAttributes(product).concat({
                                type,
                                value: '',
                                evidence: null,
                            }),
                        }
                        : product
                )),
            })
            this.focusAttribute(productIndex, attributeIndex)
        },
        removeAttribute(productIndex, attributeIndex) {
            const product = this.products[productIndex]
            if (productAttributes(product).length <= 1) {
                return
            }

            this.emitState({
                products: this.products.map((item, currentProductIndex) => (
                    currentProductIndex === productIndex
                        ? {
                            ...item,
                            attributes: productAttributes(item)
                                .filter((attribute, currentAttributeIndex) => (
                                    currentAttributeIndex !== attributeIndex
                                )),
                        }
                        : item
                )),
            })
        },
        removeProduct(productIndex) {
            const product = this.products[productIndex]
            const extractedProductId = product && product.extractedProductId
            const links = hasIdentity(extractedProductId)
                ? this.links.filter(link => !link
                    || !hasIdentity(link.extractedProductId)
                    || String(link.extractedProductId) !== String(extractedProductId))
                : this.links

            this.emitState({
                products: this.products.filter((product, index) => index !== productIndex),
                links,
            })
        },
        attributeInputId(productIndex, attributeIndex) {
            return `guided-product-${this._uid}-${productIndex}-attribute-${attributeIndex}`
        },
        attributeInputRef(productIndex, attributeIndex) {
            return `guidedProduct${productIndex}Attribute${attributeIndex}`
        },
        focusAttribute(productIndex, attributeIndex) {
            this.$nextTick(() => {
                const reference = this.$refs[this.attributeInputRef(productIndex, attributeIndex)]
                const input = Array.isArray(reference) ? reference[0] : reference
                if (input && typeof input.focus === 'function') {
                    input.focus()
                }
            })
        },
        attributeViolationMessages(productIndex, attributeIndex) {
            const path = `[products][${productIndex}][attributes][${attributeIndex}]`

            return this.violations
                .filter(violation => violation && String(violation.path || '').includes(path))
                .map(violation => violation.message)
                .filter(Boolean)
        },
    },
}
</script>

<style lang="scss" scoped>
.guided-products-review-section__heading,
.guided-products-review-section__row,
.guided-products-review-section__attribute-heading,
.guided-products-review-section__actions {
    display: flex;
    align-items: center;
}

.guided-products-review-section__heading {
    width: 100%;
    justify-content: space-between;
    gap: 1rem;
}

.guided-products-review-section__add {
    flex: 0 0 auto;
    margin-left: auto;
}

.guided-products-review-section__rows {
    display: grid;
    gap: .75rem;
}

.guided-products-review-section__row {
    min-width: 0;
    gap: .75rem;
    padding: .75rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #fafafa;
}

.guided-products-review-section__attributes {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    gap: .75rem;
    overflow-x: auto;
    padding-bottom: .25rem;
}

.guided-products-review-section__attribute {
    flex: 1 1 14rem;
    min-width: 14rem;
}

.guided-products-review-section__attribute-heading {
    min-height: 1.75rem;
    justify-content: space-between;
    gap: .25rem;
}

.guided-products-review-section__attribute-heading label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.guided-products-review-section__actions {
    flex: 0 0 auto;
    align-self: flex-end;
    gap: .5rem;
    padding-bottom: .25rem;
}

.guided-products-review-section__remove-product {
    display: flex;
}

@media (max-width: 767.98px) {
    .guided-products-review-section__heading,
    .guided-products-review-section__row {
        align-items: stretch;
        flex-direction: column;
    }

    .guided-products-review-section__actions {
        align-self: flex-end;
    }

    .guided-products-review-section__add {
        margin-left: 0;
    }
}
</style>
